import React from 'react';
import { C, fmtCOP } from '../constants';
import {
  altaTecnico, cerrarPqr, direccionarPqr, estadoDelPuente, listarProveedores, listarPqr,
  listarTecnicos, prorrogarPqr, radicarPqr, responderPqr, tecnicoEnLinea, verPqr, verProveedor,
  verTecnico,
  cruceProveedores, cruceTecnicos,
} from '../services/empresa';
import { arqueoDeIngresos } from '../services/pagos';

// ─────────────────────────────────────────────────────────────────────────────────────────────
// EMPRESA — PQR, técnicos y proveedores, servidos por `alebas-gestion`.
//
// 🔑 Esta pantalla NO tiene datos propios: cada sección pregunta y pinta lo que responda la
// gestión. El día que el root cierre una PQR desde `app.alebas.co`, acá ya sale cerrada — no hay
// nada que sincronizar porque no hay copia.
//
// 🔴 TRES ESTADOS, nunca dos: cargando · «no se pudo preguntar» (con el motivo) · vacío real. Una
// tabla vacía porque el puente no responde se lee como «no hay quejas», y esa es una afirmación
// sobre el negocio apoyada en una consulta que nunca se hizo.
//
// 🪤 Los subcomponentes con `input` se declaran ACÁ ARRIBA, no dentro del componente padre: uno
// definido adentro se remonta en cada render y el campo pierde el foco a la primera letra. El
// repo tiene un trinquete que lo caza (`componentes-anidados.test.js`).
// ─────────────────────────────────────────────────────────────────────────────────────────────

const SEMAFORO = {
  vencida: ['#f87171', 'Vencida'],
  por_vencer: ['#F9B000', 'Por vencer'],
  al_dia: [C.teal, 'Al día'],
  atendida: [C.muted, 'Atendida'],
};

function Chip({ children, color }) {
  return (
    <span style={{
      fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
      color: color || C.muted, background: 'transparent',
      border: `1px solid ${color ? `${color}55` : C.border}`, borderRadius: 5, padding: '3px 7px',
      whiteSpace: 'nowrap',
    }}>{children}</span>
  );
}

/**
 * El estado de una sección. `error` NUNCA se pinta como lista vacía: dice qué pasó y quién lo
 * puede arreglar (casi siempre, una variable que falta en el hub-api).
 */
function Estado({ cargando, error, vacio, children }) {
  if (cargando) return <div style={{ color: C.muted, fontSize: 12, padding: '18px 4px' }}>Consultando la gestión…</div>;
  if (error) {
    return (
      <div style={{ background: '#f8717111', border: '1px solid #f8717144', borderRadius: 8, padding: '12px 14px' }}>
        <div style={{ color: '#f87171', fontWeight: 700, fontSize: 12, marginBottom: 4 }}>No se pudo preguntar a la gestión</div>
        <div style={{ color: C.text, fontSize: 11.5 }}>{error}</div>
        <div style={{ color: C.muted, fontSize: 10.5, marginTop: 6 }}>
          Esto no significa que no haya registros: significa que no se pudo consultar.
        </div>
      </div>
    );
  }
  if (vacio) return <div style={{ color: C.muted, fontSize: 12, padding: '18px 4px' }}>Sin registros.</div>;
  return children;
}

function FormRadicar({ ss, onListo }) {
  const [f, setF] = React.useState({ nombre: '', correo: '', telefono: '', asunto: '', detalle: '', tipo: 'peticion', canal: 'telefono' });
  const [msg, setMsg] = React.useState(null);
  const [enviando, setEnviando] = React.useState(false);
  const set = (k) => (e) => setF(v => ({ ...v, [k]: e.target.value }));

  const enviar = async () => {
    setEnviando(true); setMsg(null);
    try {
      const r = await radicarPqr(f);
      setMsg({ ok: true, txt: `Radicada: ${r?.radicado || 'sin número'}` });
      setF({ nombre: '', correo: '', telefono: '', asunto: '', detalle: '', tipo: 'peticion', canal: 'telefono' });
      if (onListo) onListo();
    } catch (e) {
      setMsg({ ok: false, txt: e?.message || 'No se pudo radicar' });
    } finally { setEnviando(false); }
  };

  return (
    <div style={ss.card}>
      <div style={{ fontWeight: 600, color: C.heading, marginBottom: 4, fontSize: 13 }}>Radicar la que llegó por otro canal</div>
      <div style={{ fontSize: 10.5, color: C.muted, marginBottom: 11 }}>
        Por teléfono, correo o en el punto. Sin radicarla, el término no corre y se incumple sin darse cuenta.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 9 }}>
        <div><label style={ss.lbl}>Quién presenta</label><input style={ss.inp} value={f.nombre} onChange={set('nombre')} /></div>
        <div><label style={ss.lbl}>Correo</label><input style={ss.inp} value={f.correo} onChange={set('correo')} /></div>
        <div><label style={ss.lbl}>Teléfono</label><input style={ss.inp} value={f.telefono} onChange={set('telefono')} /></div>
        <div>
          <label style={ss.lbl}>Tipo</label>
          <select style={ss.inp} value={f.tipo} onChange={set('tipo')}>
            {['peticion', 'queja', 'reclamo', 'sugerencia', 'consulta', 'pedido', 'felicitacion'].map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label style={ss.lbl}>Canal</label>
          <select style={ss.inp} value={f.canal} onChange={set('canal')}>
            {['telefono', 'correo', 'escrito', 'buzon', 'verbal', 'web'].map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>
      <div style={{ marginTop: 9 }}><label style={ss.lbl}>Asunto</label><input style={ss.inp} value={f.asunto} onChange={set('asunto')} /></div>
      <div style={{ marginTop: 9 }}><label style={ss.lbl}>Detalle</label><textarea style={{ ...ss.inp, minHeight: 70, resize: 'vertical' }} value={f.detalle} onChange={set('detalle')} /></div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 11 }}>
        <button style={{ ...ss.btn, opacity: enviando ? 0.6 : 1 }} onClick={enviar} disabled={enviando}>
          {enviando ? 'Radicando…' : 'Radicar'}
        </button>
        {/* El correo o el teléfono no son opcionales: sin a dónde responder no se puede atender. */}
        <span style={{ fontSize: 10.5, color: C.muted }}>Se exige correo o teléfono: es a dónde va la respuesta.</span>
      </div>
      {msg && <div style={{ marginTop: 9, fontSize: 11.5, color: msg.ok ? C.teal : '#f87171' }}>{msg.txt}</div>}
    </div>
  );
}

function FormTecnico({ ss, onListo }) {
  const [f, setF] = React.useState({ documento: '', nombre: '', correo: '', telefono: '', ciudad: '', especialidad: '' });
  const [msg, setMsg] = React.useState(null);
  const set = (k) => (e) => setF(v => ({ ...v, [k]: e.target.value }));

  const enviar = async () => {
    setMsg(null);
    try {
      const r = await altaTecnico(f);
      setMsg({ ok: true, txt: r?.ya_estaba ? 'Ya estaba en el padrón: quedó habilitado en Solar Hub.' : 'Dado de alta en el padrón de la suite.' });
      setF({ documento: '', nombre: '', correo: '', telefono: '', ciudad: '', especialidad: '' });
      if (onListo) onListo();
    } catch (e) {
      setMsg({ ok: false, txt: e?.message || 'No se pudo dar de alta' });
    }
  };

  return (
    <div style={ss.card}>
      <div style={{ fontWeight: 600, color: C.heading, marginBottom: 4, fontSize: 13 }}>Registrar un técnico</div>
      <div style={{ fontSize: 10.5, color: C.muted, marginBottom: 11 }}>
        Entra al padrón de TODA la suite, no solo al de Solar Hub. Si ya existe, no se le pisa la ficha:
        solo queda habilitado acá. El documento es el mismo con el que se le gira y se le certifica.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 9 }}>
        <div><label style={ss.lbl}>Documento</label><input style={ss.inp} value={f.documento} onChange={set('documento')} /></div>
        <div><label style={ss.lbl}>Nombre</label><input style={ss.inp} value={f.nombre} onChange={set('nombre')} /></div>
        <div><label style={ss.lbl}>Correo</label><input style={ss.inp} value={f.correo} onChange={set('correo')} /></div>
        <div><label style={ss.lbl}>Teléfono</label><input style={ss.inp} value={f.telefono} onChange={set('telefono')} /></div>
        <div><label style={ss.lbl}>Ciudad</label><input style={ss.inp} value={f.ciudad} onChange={set('ciudad')} /></div>
        <div><label style={ss.lbl}>Especialidad</label><input style={ss.inp} value={f.especialidad} onChange={set('especialidad')} /></div>
      </div>
      <button style={{ ...ss.btn, marginTop: 11 }} onClick={enviar}>Registrar</button>
      {msg && <div style={{ marginTop: 9, fontSize: 11.5, color: msg.ok ? C.teal : '#f87171' }}>{msg.txt}</div>}
    </div>
  );
}

function FilaPqr({ p, ss, onCambio }) {
  const [abierto, setAbierto] = React.useState(false);
  const [texto, setTexto] = React.useState('');
  const [msg, setMsg] = React.useState(null);
  const [dir, setDir] = React.useState({ area: p.area || '', responsable: p.responsable || '' });
  const [pro, setPro] = React.useState({ hasta: '', motivo: '' });
  const [detalle, setDetalle] = React.useState(null);
  const [color, etiqueta] = SEMAFORO[p.plazo?.estado_plazo] || [C.muted, '—'];

  /**
   * Al abrir se pide el detalle, que trae la BITÁCORA. No es un adorno: el procedimiento se
   * sustenta con ella —quién direccionó, quién respondió, cuándo se prorrogó y por qué—, y sin
   * verla se responde a ciegas una PQR que ya tocó otra persona.
   */
  const abrir = async () => {
    if (abierto) { setAbierto(false); return; }
    setAbierto(true);
    setMsg(null);
    try { setDetalle(await verPqr(p.radicado)); }
    catch (e) { setMsg({ ok: false, txt: `No se pudo leer la bitácora: ${e?.message || ''}` }); }
  };

  const responder = async () => {
    setMsg(null);
    try {
      const r = await responderPqr(p.radicado, texto);
      // La gestión además se la MANDA a quien radicó; si no pudo, dice por qué y eso se muestra.
      setMsg({ ok: true, txt: r?.notificado ? `Respondida y enviada a ${r.notificado.a}` : `Respondida. ${r?.sin_notificar || ''}` });
      setTexto('');
      if (onCambio) onCambio();
    } catch (e) { setMsg({ ok: false, txt: e?.message || 'No se pudo responder' }); }
  };

  const cerrar = async () => {
    setMsg(null);
    try {
      await cerrarPqr(p.radicado, {});
      if (onCambio) onCambio();
    } catch (e) { setMsg({ ok: false, txt: e?.message || 'No se pudo cerrar' }); }
  };

  const direccionar = async () => {
    setMsg(null);
    try {
      await direccionarPqr(p.radicado, dir);
      setMsg({ ok: true, txt: 'Direccionada.' });
      if (onCambio) onCambio();
    } catch (e) { setMsg({ ok: false, txt: e?.message || 'No se pudo direccionar' }); }
  };

  const prorrogar = async () => {
    setMsg(null);
    try {
      await prorrogarPqr(p.radicado, pro);
      setMsg({ ok: true, txt: `Prórroga hasta ${pro.hasta}.` });
      setPro({ hasta: '', motivo: '' });
      if (onCambio) onCambio();
    } catch (e) { setMsg({ ok: false, txt: e?.message || 'No se pudo prorrogar' }); }
  };

  return (
    <>
      <tr>
        <td style={ss.td}><code style={{ fontSize: 10.5 }}>{p.radicado}</code></td>
        <td style={ss.td}>{p.tipo}</td>
        <td style={ss.td}>{p.asunto}</td>
        <td style={ss.td}>{p.estado}</td>
        <td style={ss.td}><Chip color={color}>{etiqueta}</Chip></td>
        <td style={ss.td}>{p.vence_en || '—'}</td>
        <td style={ss.td}>
          <button style={{ ...ss.btn, background: 'transparent', color: C.teal, border: `1px solid ${C.border}` }} onClick={abrir}>
            {abierto ? 'Cerrar' : 'Atender'}
          </button>
        </td>
      </tr>
      {abierto && (
        <tr>
          <td style={{ ...ss.td, background: C.dark }} colSpan={7}>
            <div style={{ fontSize: 11.5, color: C.text, marginBottom: 8, whiteSpace: 'pre-wrap' }}>{p.detalle}</div>
            {detalle?.movimientos?.length > 0 && (
              <div style={{ marginBottom: 10, paddingLeft: 10, borderLeft: `2px solid ${C.border}` }}>
                {detalle.movimientos.map(m => (
                  <div key={m.id} style={{ fontSize: 10.5, color: C.muted, marginBottom: 2 }}>
                    <strong style={{ color: C.text }}>{m.accion}</strong> · {m.quien}
                    {m.detalle ? ` · ${m.detalle}` : ''}
                  </div>
                ))}
              </div>
            )}
            <textarea
              style={{ ...ss.inp, minHeight: 64, resize: 'vertical' }}
              placeholder="Respuesta de fondo…"
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <button style={ss.btn} onClick={responder}>Responder</button>
              <button style={{ ...ss.btn, background: 'transparent', color: C.muted, border: `1px solid ${C.border}` }} onClick={cerrar}>Cerrar el caso</button>
              {/* Responder no cierra: en el procedimiento son dos pasos, y la gestión rechaza
                  cerrar sin haber respondido — cerrar sin respuesta es cerrar un incumplimiento. */}
              <span style={{ fontSize: 10.5, color: C.muted }}>Responder no cierra. No se puede cerrar sin haber respondido.</span>
            </div>

            {/* Direccionar y prorrogar son pasos del MISMO procedimiento, y sin ellos la pantalla
                obliga a salirse a otra consola para media atención. */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 9, marginTop: 12, paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
              <div><label style={ss.lbl}>Área</label><input style={ss.inp} value={dir.area} onChange={(e) => setDir(v => ({ ...v, area: e.target.value }))} /></div>
              <div><label style={ss.lbl}>Responsable</label><input style={ss.inp} value={dir.responsable} onChange={(e) => setDir(v => ({ ...v, responsable: e.target.value }))} /></div>
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <button style={{ ...ss.btn, background: 'transparent', color: C.teal, border: `1px solid ${C.border}` }} onClick={direccionar}>Direccionar</button>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 9, marginTop: 9 }}>
              <div><label style={ss.lbl}>Prorrogar hasta</label><input style={ss.inp} placeholder="AAAA-MM-DD" value={pro.hasta} onChange={(e) => setPro(v => ({ ...v, hasta: e.target.value }))} /></div>
              <div><label style={ss.lbl}>Motivo de la demora</label><input style={ss.inp} value={pro.motivo} onChange={(e) => setPro(v => ({ ...v, motivo: e.target.value }))} /></div>
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <button style={{ ...ss.btn, background: 'transparent', color: '#F9B000', border: `1px solid ${C.border}` }} onClick={prorrogar}>Prorrogar</button>
              </div>
            </div>
            {/* El compromiso interno se puede mover; el término de LEY no. Lo rechaza la gestión. */}
            <div style={{ fontSize: 10.5, color: C.muted, marginTop: 6 }}>
              Sin motivo escrito no hay prórroga, y la fecha no puede pasarse del término de ley.
            </div>
            {msg && <div style={{ marginTop: 8, fontSize: 11.5, color: msg.ok ? C.teal : '#f87171' }}>{msg.txt}</div>}
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Una fila del padrón. El detalle se pide bajo demanda porque trae lo caro y lo importante: **si
 * a esa persona se le puede girar**.
 *
 * 🔑 Hoy eso se descubre TARDE — al cerrar la obra, cuando el 80 % ya se liquidó y la cuenta de
 * cobro rebota con `sin_documento`. Acá se puede mirar antes de asignarle el trabajo.
 */
function FilaTecnico({ t, ss, onCambio }) {
  const [ficha, setFicha] = React.useState(null);
  const [msg, setMsg] = React.useState(null);
  const enSolarHub = (t.lineas || []).find(l => l.linea === 'solarhub');

  const ver = async () => {
    if (ficha) { setFicha(null); return; }
    setMsg(null);
    try { setFicha(await verTecnico(t.documento)); }
    catch (e) { setMsg({ ok: false, txt: e?.message || 'No se pudo leer la ficha' }); }
  };

  const alternar = async () => {
    setMsg(null);
    try {
      await tecnicoEnLinea(t.documento, !(enSolarHub && enSolarHub.estado === 'activo'));
      if (onCambio) onCambio();
    } catch (e) { setMsg({ ok: false, txt: e?.message || 'No se pudo cambiar' }); }
  };

  const c = ficha?.contratista;
  return (
    <>
      <tr>
        <td style={ss.td}><code style={{ fontSize: 10.5 }}>{t.documento}</code></td>
        <td style={ss.td}>{t.nombre}</td>
        <td style={ss.td}>{t.correo || '—'}</td>
        <td style={ss.td}>
          <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
            {(t.lineas || []).length === 0
              ? <span style={{ color: C.muted }}>—</span>
              : t.lineas.map(l => <Chip key={l.linea} color={l.estado === 'activo' ? C.teal : C.muted}>{l.linea}</Chip>)}
          </span>
        </td>
        <td style={ss.td}><Chip color={t.estado === 'activo' ? C.teal : '#f87171'}>{t.estado}</Chip></td>
        <td style={ss.td}>
          <span style={{ display: 'inline-flex', gap: 6 }}>
            <button style={{ ...ss.btn, background: 'transparent', color: C.teal, border: `1px solid ${C.border}` }} onClick={ver}>
              {ficha ? 'Ocultar' : 'Ficha'}
            </button>
            <button style={{ ...ss.btn, background: 'transparent', color: C.muted, border: `1px solid ${C.border}` }} onClick={alternar}>
              {enSolarHub && enSolarHub.estado === 'activo' ? 'Sacar de Solar Hub' : 'Habilitar acá'}
            </button>
          </span>
        </td>
      </tr>
      {(ficha || msg) && (
        <tr>
          <td style={{ ...ss.td, background: C.dark }} colSpan={6}>
            {msg && <div style={{ fontSize: 11.5, color: '#f87171' }}>{msg.txt}</div>}
            {c && (
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', fontSize: 11.5, color: C.text }}>
                <Chip color={c.puede_cobrar ? C.teal : '#F9B000'}>
                  {c.puede_cobrar ? 'Se le puede girar' : 'Todavía no se le puede girar'}
                </Chip>
                <span>{c.inscrito ? 'Inscrito como contratista' : c.detalle}</span>
                {/* El detalle de POR QUÉ no puede cobrar nombra documentos de la persona: es del
                    root, y con la llave de la línea la gestión no lo manda. Acá basta el sí/no. */}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function FilaProveedor({ p, ss }) {
  const [detalle, setDetalle] = React.useState(null);
  const [msg, setMsg] = React.useState(null);

  const ver = async () => {
    if (detalle) { setDetalle(null); return; }
    setMsg(null);
    try { setDetalle(await verProveedor(p.nit)); }
    catch (e) { setMsg({ ok: false, txt: e?.message || 'No se pudo leer' }); }
  };

  return (
    <>
      <tr>
        <td style={ss.td}><code style={{ fontSize: 10.5 }}>{p.nit}</code></td>
        <td style={ss.td}>{p.nombre}</td>
        <td style={ss.td}>{p.oficio || '—'}</td>
        <td style={ss.td}>{p.ciudad || '—'}</td>
        <td style={ss.td}><Chip color={p.estado === 'inscrito' ? C.teal : '#f87171'}>{p.estado}</Chip></td>
        <td style={ss.td}>
          <button style={{ ...ss.btn, background: 'transparent', color: C.teal, border: `1px solid ${C.border}` }} onClick={ver}>
            {detalle ? 'Ocultar' : '¿Se le puede pagar?'}
          </button>
        </td>
      </tr>
      {(detalle || msg) && (
        <tr>
          <td style={{ ...ss.td, background: C.dark }} colSpan={6}>
            {msg && <div style={{ fontSize: 11.5, color: '#f87171' }}>{msg.txt}</div>}
            {detalle && (
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', fontSize: 11.5, color: C.text }}>
                <Chip color={detalle.puede_cobrar ? C.teal : '#F9B000'}>
                  {detalle.puede_cobrar ? 'Se le puede girar' : 'Todavía no'}
                </Chip>
                <span>{detalle.correo || 'sin correo'} · {detalle.telefono || 'sin teléfono'}</span>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * ARQUEO DE INGRESOS — «llevar y auditar todos los ingresos» (dirección del dueño, 7-sep-2026).
 *
 * 🔑 La pregunta que responde no es «cuánto vendimos» sino **«¿todo lo cobrado quedó facturado?»**.
 * Un pedido pagado sin factura es plata que entró y que la contabilidad no tiene, y hasta hoy eso
 * solo se veía abriendo los pedidos uno por uno.
 *
 * 🪤 Y si el arqueo no se pudo hacer, se DICE: no se pinta un cero, que se leería como «no entró
 * nada» (y con `cuadra` en null, tampoco se afirma que cuadre).
 */
/**
 * QUIÉN TRABAJA PARA SOLAR HUB Y LA EMPRESA NO LO TIENE.
 *
 * 🔑 Un técnico entra al padrón cuando registra a dónde le giran. El que no lo hizo **no existe
 * para la empresa**, y eso no se nota hasta el día del giro: obra cerrada, plata liquidada y el
 * pago esperando. Esta tarjeta lo muestra antes.
 */
function CruceTecnicos({ ss }) {
  const [estado, setEstado] = React.useState({ cargando: true, error: null, datos: null });

  const cargar = React.useCallback(async () => {
    setEstado((s) => ({ ...s, cargando: true, error: null }));
    try { setEstado({ cargando: false, error: null, datos: await cruceTecnicos() }); }
    catch (e) { setEstado({ cargando: false, error: e?.message || 'Error', datos: null }); }
  }, []);

  React.useEffect(() => { cargar(); }, [cargar]);

  const d = estado.datos;
  const sinMedio = (d && d.sin_medio) || [];
  const sinPadron = (d && d.sin_padron) || [];

  return (
    <div style={{ ...ss.card, marginTop: 14 }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: C.heading, marginBottom: 4 }}>
        Técnicos de Solar Hub contra el padrón
      </div>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 10, lineHeight: 1.5 }}>
        Un técnico entra al padrón <strong>cuando registra a dónde le giran</strong>. El que no lo hizo
        no existe para la empresa — y eso se descubre el día del giro, con la obra ya cerrada.
      </div>

      {estado.cargando && <div style={{ fontSize: 11, color: C.muted }}>Cruzando…</div>}
      {estado.error && <div style={{ fontSize: 11, color: '#f87171' }}>No se pudo cruzar: {estado.error}</div>}
      {d && d.cuadra === null && <div style={{ fontSize: 11, color: '#F9B000' }}>{d.motivo}</div>}

      {d && d.ok && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(120px,1fr))', gap: 9, marginBottom: 10 }}>
            {[['En Solar Hub', d.solarhub, C.text], ['En el padrón', d.padron, C.text],
              ['Ya inscritos', (d.inscritos || []).length, C.teal],
              ['Sin medio de cobro', sinMedio.length, sinMedio.length ? '#F9B000' : C.muted],
              ['Con medio y sin padrón', sinPadron.length, sinPadron.length ? '#f87171' : C.muted]].map(([l, v, col]) => (
              <div key={l} style={ss.stat}>
                <div style={{ fontSize: 9, color: C.muted, marginBottom: 4, textTransform: 'uppercase' }}>{l}</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: col }}>{v}</div>
              </div>
            ))}
          </div>

          {d.cuadra && <div style={{ fontSize: 11.5, color: C.teal }}>✓ Todos los técnicos tienen medio de cobro y están en el padrón.</div>}

          {(sinMedio.length > 0 || sinPadron.length > 0) && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>{['Técnico', 'Correo', 'Documento', 'Qué falta'].map((h) => (
                  <th key={h} style={ss.th}>{h}</th>
                ))}</tr></thead>
                <tbody>
                  {[...sinPadron, ...sinMedio].map((t) => (
                    <tr key={t.id}>
                      <td style={ss.td}>{t.nombre}</td>
                      <td style={ss.td}>{t.correo}</td>
                      <td style={ss.td}>{t.documento || '—'}</td>
                      <td style={{ ...ss.td, color: C.muted }}>{t.motivo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * A QUIÉN LE COMPRAMOS Y NO ESTÁ EN EL PADRÓN.
 *
 * 🔑 Cierra la cadena que el dueño pidió: la Tienda le compra a proveedores que la empresa no
 * tiene inscritos, y en la gestión **inscribir a quien se le gira es del root** — que además solo
 * puede inscribir a quien YA está en el padrón. Sin este cruce, el root no tenía cómo enterarse de
 * a quién le falta la ficha.
 *
 * 🪤 Un proveedor SIN NIT no se cuenta como «falta en el padrón»: no se puede cruzar. Es un
 * problema de datos de la Tienda, y decirlo aparte evita acusar a la empresa de un hueco ajeno.
 */
function CruceProveedores({ ss }) {
  const [estado, setEstado] = React.useState({ cargando: true, error: null, datos: null });

  const cargar = React.useCallback(async () => {
    setEstado((s) => ({ ...s, cargando: true, error: null }));
    try { setEstado({ cargando: false, error: null, datos: await cruceProveedores() }); }
    catch (e) { setEstado({ cargando: false, error: e?.message || 'Error', datos: null }); }
  }, []);

  React.useEffect(() => { cargar(); }, [cargar]);

  const d = estado.datos;
  const faltan = (d && d.faltan) || [];
  const sinNit = (d && d.sin_nit) || [];

  return (
    <div style={{ ...ss.card, marginTop: 14 }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: C.heading, marginBottom: 4 }}>
        Proveedores de la Tienda contra el padrón
      </div>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 10, lineHeight: 1.5 }}>
        A quién le compra Solar Hub y la empresa todavía no tiene inscrito. <strong>Inscribir es del
        root</strong> en la consola de la empresa; acá solo se ve quién falta — y el root no puede
        inscribir a alguien que no existe en el padrón.
      </div>

      {estado.cargando && <div style={{ fontSize: 11, color: C.muted }}>Cruzando…</div>}
      {estado.error && <div style={{ fontSize: 11, color: '#f87171' }}>No se pudo cruzar: {estado.error}</div>}

      {d && d.cuadra === null && (
        <div style={{ fontSize: 11, color: '#F9B000' }}>{d.motivo}</div>
      )}

      {d && d.ok && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(120px,1fr))', gap: 9, marginBottom: 10 }}>
            {[['En la Tienda', d.tienda, C.text], ['En el padrón', d.padron, C.text],
              ['Ya inscritos', (d.inscritos || []).length, C.teal],
              ['Faltan', faltan.length, faltan.length ? '#f87171' : C.muted],
              ['Sin NIT', sinNit.length, sinNit.length ? '#F9B000' : C.muted]].map(([l, v, col]) => (
              <div key={l} style={ss.stat}>
                <div style={{ fontSize: 9, color: C.muted, marginBottom: 4, textTransform: 'uppercase' }}>{l}</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: col }}>{v}</div>
              </div>
            ))}
          </div>

          {d.cuadra && <div style={{ fontSize: 11.5, color: C.teal }}>✓ Todos los proveedores de la Tienda están en el padrón.</div>}

          {(faltan.length > 0 || sinNit.length > 0) && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>{['Proveedor', 'NIT', 'Correo', 'Qué falta'].map((h) => (
                  <th key={h} style={ss.th}>{h}</th>
                ))}</tr></thead>
                <tbody>
                  {[...faltan, ...sinNit].map((p) => (
                    <tr key={`${p.id}`}>
                      <td style={ss.td}>{p.empresa}</td>
                      <td style={ss.td}>{p.nit || '—'}</td>
                      <td style={ss.td}>{p.correo}</td>
                      <td style={{ ...ss.td, color: C.muted }}>{p.motivo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ArqueoIngresos({ ss }) {
  const [estado, setEstado] = React.useState({ cargando: true, error: null, datos: null });

  const cargar = React.useCallback(async () => {
    setEstado((s) => ({ ...s, cargando: true, error: null }));
    try {
      const r = await arqueoDeIngresos({});
      setEstado({ cargando: false, error: null, datos: r });
    } catch (e) {
      setEstado({ cargando: false, error: e?.message || 'Error', datos: null });
    }
  }, []);

  React.useEffect(() => { cargar(); }, [cargar]);

  const d = estado.datos;
  const cuadra = d ? d.cuadra : null;
  const color = cuadra === true ? C.teal : (cuadra === false ? '#f87171' : C.muted);

  return (
    <div style={{ ...ss.card, marginTop: 22 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.heading }}>Ingresos del mes · arqueo</div>
        <button type="button" style={{ ...ss.btn, padding: '4px 10px', fontSize: 10 }} onClick={cargar}>
          Actualizar
        </button>
      </div>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 12, lineHeight: 1.55 }}>
        Lo que se cobró en el período y si terminó facturado. La factura la emite el motor de la suite
        contra la cuenta Siigo de Alebas con el centro de costo de Solar Hub: <strong>el libro de la
        empresa lee estos ingresos por ahí</strong>, esto dice si llegaron.
      </div>

      {estado.cargando && <div style={{ fontSize: 11, color: C.muted }}>Arqueando…</div>}
      {estado.error && (
        <div style={{ fontSize: 11, color: '#f87171' }}>No se pudo arquear: {estado.error}</div>
      )}

      {d && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 9, marginBottom: 12 }}>
            {[
              ['Recaudado', d.recaudado ? fmtCOP(d.recaudado.total_cop) : '—', C.text],
              ['Pedidos', d.recaudado ? d.recaudado.pedidos : '—', C.text],
              ['Facturado', d.facturado ? fmtCOP(d.facturado.total_cop) : '—', C.teal],
              ['Sin factura', d.sin_factura ? fmtCOP(d.sin_factura.total_cop) : '—', d.sin_factura && d.sin_factura.pedidos ? '#f87171' : C.muted],
            ].map(([l, v, col]) => (
              <div key={l} style={ss.stat}>
                <div style={{ fontSize: 9, color: C.muted, marginBottom: 4, textTransform: 'uppercase' }}>{l}</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: col }}>{v}</div>
              </div>
            ))}
          </div>

          <div style={{ fontSize: 11.5, color, fontWeight: 600, marginBottom: 8 }}>
            {cuadra === true && '✓ Todo lo cobrado en el período está facturado.'}
            {cuadra === false && `⚠️ ${d.motivo}`}
            {cuadra === null && `No se pudo arquear: ${d.motivo || 'sin detalle'}`}
          </div>

          {d.facturado_sin_recibo > 0 && (
            <div style={{ fontSize: 11, color: '#F9B000', marginBottom: 8 }}>
              {d.facturado_sin_recibo} factura(s) sin recibo de caja: la venta quedó cobrada y la cartera
              abierta en Siigo.
            </div>
          )}

          {d.sin_factura && d.sin_factura.detalle && d.sin_factura.detalle.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead>
                <tr>{['Pedido', 'Tipo', 'Valor', 'Por qué no está facturado'].map((h) => (
                  <th key={h} style={ss.th}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {d.sin_factura.detalle.map((p) => (
                  <tr key={p.id}>
                    <td style={ss.td}>{p.id}</td>
                    <td style={ss.td}>{p.tipo}</td>
                    <td style={ss.td}>{fmtCOP(p.total_cop)}</td>
                    <td style={{ ...ss.td, color: C.muted }}>{p.motivo}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}

export default function Empresa({ ss }) {
  const [puente, setPuente] = React.useState(null);
  const [pqr, setPqr] = React.useState({ cargando: true, error: null, datos: null });
  const [tec, setTec] = React.useState({ cargando: true, error: null, datos: null });
  const [prov, setProv] = React.useState({ cargando: true, error: null, datos: null });

  const cargarPqr = React.useCallback(async () => {
    setPqr(s => ({ ...s, cargando: true, error: null }));
    try { setPqr({ cargando: false, error: null, datos: await listarPqr() }); }
    catch (e) { setPqr({ cargando: false, error: e?.message || 'Error', datos: null }); }
  }, []);

  const cargarTec = React.useCallback(async () => {
    setTec(s => ({ ...s, cargando: true, error: null }));
    try { setTec({ cargando: false, error: null, datos: await listarTecnicos() }); }
    catch (e) { setTec({ cargando: false, error: e?.message || 'Error', datos: null }); }
  }, []);

  const cargarProv = React.useCallback(async () => {
    setProv(s => ({ ...s, cargando: true, error: null }));
    try { setProv({ cargando: false, error: null, datos: await listarProveedores() }); }
    catch (e) { setProv({ cargando: false, error: e?.message || 'Error', datos: null }); }
  }, []);

  React.useEffect(() => {
    estadoDelPuente().then(setPuente).catch(() => setPuente({ configurado: false, falta: ['no se pudo consultar'] }));
    cargarPqr(); cargarTec(); cargarProv();
  }, [cargarPqr, cargarTec, cargarProv]);

  const lista = pqr.datos?.pqr || [];
  const resumen = pqr.datos?.resumen || {};
  const tecnicos = tec.datos?.tecnicos || [];
  const proveedores = prov.datos?.proveedores || [];

  return (
    <div>
      <div style={ss.h2}>PQR y padrón de la empresa</div>
      <div style={{ fontSize: 11.5, color: C.muted, marginBottom: 14, maxWidth: 760 }}>
        Estas tres cosas viven en <strong>la gestión de la empresa</strong>, no acá: son las mismas que ve
        el root en <code>app.alebas.co</code> y las mismas que consume FluxAI. Solar Hub no guarda copia.
      </div>

      {/* El puente: si falta una variable se DICE, en vez de mostrar tablas en cero. */}
      {puente && !puente.configurado && (
        <div style={{ background: '#F9B00011', border: '1px solid #F9B00044', borderRadius: 8, padding: '12px 14px', marginBottom: 14 }}>
          <div style={{ color: '#F9B000', fontWeight: 700, fontSize: 12, marginBottom: 4 }}>El puente con la gestión no está conectado</div>
          <div style={{ color: C.text, fontSize: 11.5 }}>
            Faltan {(puente.falta || []).join(' y ')} en el hub-api. Mientras tanto no se puede leer ni radicar
            — y lo que se ve abajo no es «no hay registros».
          </div>
        </div>
      )}

      {/* ─── PQR ─────────────────────────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(120px,1fr))', gap: 9, marginBottom: 12 }}>
        {[['Vencidas', resumen.vencidas, '#f87171'], ['Por vencer', resumen.por_vencer, '#F9B000'],
          ['Al día', resumen.al_dia, C.teal], ['Atendidas', resumen.atendidas, C.muted]].map(([l, v, col]) => (
          <div key={l} style={ss.stat}>
            <div style={{ fontSize: 9, color: C.muted, marginBottom: 4, textTransform: 'uppercase' }}>{l}</div>
            {/* Un 0 con el puente caído afirmaría que no hay quejas: sin dato va raya. */}
            <div style={{ fontSize: 18, fontWeight: 700, color: col }}>{pqr.datos ? (v ?? 0) : '—'}</div>
          </div>
        ))}
      </div>

      <div style={ss.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 11 }}>
          <div style={{ fontWeight: 600, color: C.heading, fontSize: 13 }}>PQR de Solar Hub</div>
          <button style={{ ...ss.btn, background: 'transparent', color: C.muted, border: `1px solid ${C.border}` }} onClick={cargarPqr}>Actualizar</button>
        </div>
        <Estado cargando={pqr.cargando} error={pqr.error} vacio={!!pqr.datos && lista.length === 0}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>{['Radicado', 'Tipo', 'Asunto', 'Estado', 'Plazo', 'Vence', ''].map(h => <th key={h} style={ss.th}>{h}</th>)}</tr></thead>
              <tbody>{lista.map(p => <FilaPqr key={p.radicado} p={p} ss={ss} onCambio={cargarPqr} />)}</tbody>
            </table>
          </div>
        </Estado>
      </div>

      <FormRadicar ss={ss} onListo={cargarPqr} />

      {/* ─── TÉCNICOS ────────────────────────────────────────────────────────────────── */}
      <div style={ss.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <div style={{ fontWeight: 600, color: C.heading, fontSize: 13 }}>Padrón de técnicos de la suite</div>
          <button style={{ ...ss.btn, background: 'transparent', color: C.muted, border: `1px solid ${C.border}` }} onClick={cargarTec}>Actualizar</button>
        </div>
        <div style={{ fontSize: 10.5, color: C.muted, marginBottom: 11 }}>
          Es UNO solo para Solar Hub, FluxAI y la gestión. Antes la misma persona existía tres veces y
          se la podía suspender en una y seguirle girando por otra.
        </div>
        <Estado cargando={tec.cargando} error={tec.error} vacio={!!tec.datos && tecnicos.length === 0}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>{['Documento', 'Nombre', 'Correo', 'Opera en', 'Estado', ''].map(h => <th key={h} style={ss.th}>{h}</th>)}</tr></thead>
              <tbody>{tecnicos.map(t => <FilaTecnico key={t.documento} t={t} ss={ss} onCambio={cargarTec} />)}</tbody>
            </table>
          </div>
        </Estado>

        {/* Quién trabaja para Solar Hub y la empresa no lo tiene. */}
        <CruceTecnicos ss={ss} />
      </div>

      <FormTecnico ss={ss} onListo={cargarTec} />

      {/* ─── PROVEEDORES ─────────────────────────────────────────────────────────────── */}
      <div style={ss.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <div style={{ fontWeight: 600, color: C.heading, fontSize: 13 }}>Terceros inscritos en la empresa</div>
          <button style={{ ...ss.btn, background: 'transparent', color: C.muted, border: `1px solid ${C.border}` }} onClick={cargarProv}>Actualizar</button>
        </div>
        <div style={{ fontSize: 10.5, color: C.muted, marginBottom: 11 }}>
          A quién puede pagarle la empresa. No es el portal de proveedores de la tienda: esto es el padrón
          de terceros, donde vive «inscribir antes de pagar». Los datos de giro son del root y no se ven acá.
        </div>
        <Estado cargando={prov.cargando} error={prov.error} vacio={!!prov.datos && proveedores.length === 0}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>{['NIT / documento', 'Nombre', 'Oficio', 'Ciudad', 'Estado', ''].map(h => <th key={h} style={ss.th}>{h}</th>)}</tr></thead>
              <tbody>{proveedores.map(p => <FilaProveedor key={p.nit} p={p} ss={ss} />)}</tbody>
            </table>
          </div>
        </Estado>

        {/* El cruce: a quién le compramos y la empresa no tiene inscrito. */}
        <CruceProveedores ss={ss} />
      </div>

      {/* Los ingresos: lo que entró y si quedó facturado. Cierra el ciclo que ya trae PQR,
          técnicos y proveedores desde la gestión. */}
      <ArqueoIngresos ss={ss} />
    </div>
  );
}
